import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_WORK_ORDER_COMMENTS } from '../../../graphql/queries';
import { 
  CREATE_WORK_ORDER_COMMENT, 
  UPDATE_WORK_ORDER_COMMENT, 
  DELETE_WORK_ORDER_COMMENT,
  MARK_WORK_ORDER_COMMENTS_READ 
} from '../../../graphql/mutations';
import { API_URL } from '../../../config';
import WorkOrderCommentItem from './WorkOrderCommentItem';
import WorkOrderCommentForm from './WorkOrderCommentForm';

export default function WorkOrderCommentThread({ 
  workOrderId, 
  workOrder,
  currentUser 
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Query comments
  const { data, loading, error, refetch } = useQuery(GET_WORK_ORDER_COMMENTS, {
    variables: { workOrderId, limit: 100, offset: 0 },
    fetchPolicy: 'network-only'
  });

  // Mutations
  const [createComment] = useMutation(CREATE_WORK_ORDER_COMMENT);
  const [updateComment] = useMutation(UPDATE_WORK_ORDER_COMMENT);
  const [deleteComment] = useMutation(DELETE_WORK_ORDER_COMMENT);
  const [markAsRead] = useMutation(MARK_WORK_ORDER_COMMENTS_READ);

  const comments = data?.workOrderComments?.comments || [];
  const total = data?.workOrderComments?.total || 0;

  // Mark as read when comments are loaded
  useEffect(() => {
    if (comments.length > 0 && currentUser) {
      markAsRead({ variables: { workOrderId } }).catch(() => {
        // Silently fail
      });
    }
  }, [comments.length, workOrderId, currentUser, markAsRead]);

  // Users can always mark comments as urgent
  const canEscalate = true;

  // Handle photo upload
  const uploadPhoto = async (photoFile) => {
    try {
      // Step 1: Get presigned upload URL from backend
      const response = await fetch(`${API_URL}/api/work-orders/get-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          fileName: photoFile.name,
          fileType: photoFile.type
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.statusText}`);
      }

      const { uploadUrl, imageUrl, headers } = await response.json();

      // Step 2: Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: headers,
        body: photoFile
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      // Return the public image URL
      return imageUrl;
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
      return null;
    }
  };

  // Handle new comment submission
  const handleSubmitComment = async ({ comment, isEscalation, photoFile }) => {
    setIsSubmitting(true);

    try {
      let attachments = [];

      // Upload photo if provided
      if (photoFile) {
        const photoUrl = await uploadPhoto(photoFile);
        if (photoUrl) {
          attachments.push(photoUrl);
        }
      }

      // Create comment
      await createComment({
        variables: {
          input: {
            workOrderId,
            comment,
            isEscalation,
            attachments
          }
        }
      });

      toast.success(isEscalation ? 'Escalation sent to management' : 'Comment posted');
      refetch();
    } catch (error) {
      console.error('Error creating comment:', error);
      toast.error(error.message || 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit comment
  const handleEditComment = async (commentId, newText) => {
    try {
      await updateComment({
        variables: {
          id: commentId,
          input: { comment: newText }
        }
      });

      toast.success('Comment updated');
      refetch();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment({
        variables: { id: commentId }
      });

      toast.success('Comment deleted');
      refetch();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error loading comments: {error.message}
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg mt-6">
      {/* Header */}
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Discussion {total > 0 && `(${total})`}
          </h3>
          <button
            onClick={() => refetch()}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            disabled={loading}
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
        <p className="text-sm text-gray-600">
          If there is an issue or this work order needs to be escalated for approval, send a comment below to the Property Manager and Admin for approval or direction.
        </p>
      </div>

      {/* Comments List */}
      <div className="px-4 py-5 sm:p-6">
        {loading && comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="font-medium">No comments yet</p>
            <p className="text-sm mt-1">Be the first to comment on this work order</p>
          </div>
        ) : (
          <div className="space-y-1">
            {comments.map((comment) => (
              <WorkOrderCommentItem
                key={comment.id}
                comment={comment}
                currentUser={currentUser}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
              />
            ))}
          </div>
        )}

        {/* Comment Form */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <WorkOrderCommentForm
            workOrderId={workOrderId}
            onSubmit={handleSubmitComment}
            canEscalate={canEscalate}
            isLoading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
}
